import { assertNeverType } from '@common/neo.tax';

import { TICKETING_PROVIDER_NAMES } from '../constants';
import {
  GetTicketingConnectionsQuery,
  GitHubInstallationStatus,
  TicketingAcquisitionMethod,
  TicketingProvider,
} from '../graphql';

/** A connection as the Data Sources list reads it — the scoped query's shape, not GET_COMPANY's. */
export type CompanyTicketingToken = GetTicketingConnectionsQuery['getCompany']['ticketingTokens'][number];

/** The states a connection row can be in on the Data Sources page. */
export enum TicketingConnectionStatus {
  Connected = 'Connected',
  SupersededByGitHubApp = 'SupersededByGitHubApp',
  NeedsRelink = 'NeedsRelink',
  WaitingForInstall = 'WaitingForInstall',
  SuspendedInGitHub = 'SuspendedInGitHub',
  UninstalledFromGitHub = 'UninstalledFromGitHub',
}

/** The badge each state gets. Kept out of the enum so rewording copy cannot change an identity. */
export const CONNECTION_STATUS_LABELS: Record<TicketingConnectionStatus, string> = {
  [TicketingConnectionStatus.Connected]: 'Connected',
  [TicketingConnectionStatus.SupersededByGitHubApp]: 'Superseded by GitHub App',
  [TicketingConnectionStatus.NeedsRelink]: 'Needs relink',
  [TicketingConnectionStatus.WaitingForInstall]: 'Waiting for GitHub install',
  [TicketingConnectionStatus.SuspendedInGitHub]: 'Suspended in GitHub',
  [TicketingConnectionStatus.UninstalledFromGitHub]: 'Uninstalled from GitHub',
};

export enum TicketingConnectionAction {
  MigrateToGitHubApp = 'MigrateToGitHubApp',
  Relink = 'Relink',
  ResumeInstall = 'ResumeInstall',
  Reconnect = 'Reconnect',
  Disconnect = 'Disconnect',
}

/** How the connection was made, in the customer's terms rather than the enum's. */
export const CONNECTION_METHOD_NAMES: Record<TicketingAcquisitionMethod, string> = {
  [TicketingAcquisitionMethod.GithubApp]: 'GitHub App',
  [TicketingAcquisitionMethod.MergeDev]: 'Personal access token (Merge)',
  [TicketingAcquisitionMethod.Snowflake]: 'Snowflake',
  // Never rendered: file uploads are not connections and are filtered out below.
  [TicketingAcquisitionMethod.File]: 'File upload',
};

export type TicketingConnectionRow = {
  id: string;
  provider: string;
  method: string;
  organization: string | null;
  status: TicketingConnectionStatus;
  connectedAt: string;
  lastSuccessfulSync: CompanyTicketingToken['lastSuccessfulSync'];
  actions: TicketingConnectionAction[];
  token: CompanyTicketingToken;
};

const isGitHubApp = (token: CompanyTicketingToken): boolean =>
  token.acquisitionMethod === TicketingAcquisitionMethod.GithubApp;

/** A Merge-managed GitHub connection — the one kind the App can supersede. */
const isMergeGitHub = (token: CompanyTicketingToken): boolean =>
  token.acquisitionMethod === TicketingAcquisitionMethod.MergeDev &&
  token.ticketingProvider === TicketingProvider.GithubIssues;

const isActiveGitHubApp = (token: CompanyTicketingToken): boolean =>
  isGitHubApp(token) && token.installationStatus === GitHubInstallationStatus.Active;

const gitHubAppStatus = (installationStatus: GitHubInstallationStatus | null): TicketingConnectionStatus => {
  switch (installationStatus) {
    case GitHubInstallationStatus.Active:
      return TicketingConnectionStatus.Connected;
    case GitHubInstallationStatus.Suspended:
      return TicketingConnectionStatus.SuspendedInGitHub;
    case GitHubInstallationStatus.Deleted:
      return TicketingConnectionStatus.UninstalledFromGitHub;
    // A GITHUB_APP row is armed as PENDING before the browser even leaves for GitHub, so a null
    // status reads the same way: the install has not been confirmed.
    case GitHubInstallationStatus.Pending:
    case null:
      return TicketingConnectionStatus.WaitingForInstall;
    default:
      return assertNeverType(installationStatus, 'GitHub installation status');
  }
};

const gitHubAppActions = (
  installationStatus: GitHubInstallationStatus | null,
  { gitHubAppAvailable }: { gitHubAppAvailable: boolean },
): TicketingConnectionAction[] => {
  // Both repairs are an install, and an environment with no App configured cannot complete one. A
  // company can still hold an App connection there — from another environment, or from before the
  // config was removed — so the row is real; the repair is not on offer.
  if (!gitHubAppAvailable) {
    return [TicketingConnectionAction.Disconnect];
  }

  switch (gitHubAppStatus(installationStatus)) {
    case TicketingConnectionStatus.WaitingForInstall:
      return [TicketingConnectionAction.ResumeInstall, TicketingConnectionAction.Disconnect];
    // A fresh install is the only way back from an uninstall, and re-arming is what makes GitHub
    // fire `installation.created` again.
    case TicketingConnectionStatus.UninstalledFromGitHub:
      return [TicketingConnectionAction.Reconnect, TicketingConnectionAction.Disconnect];
    // Unsuspending is done in GitHub, so there is nothing to offer here but removal.
    default:
      return [TicketingConnectionAction.Disconnect];
  }
};

const mergeConnectionActions = (
  token: CompanyTicketingToken,
  { canMigrateToGitHubApp }: { canMigrateToGitHubApp: boolean },
): TicketingConnectionAction[] => [
  ...(canMigrateToGitHubApp ? [TicketingConnectionAction.MigrateToGitHubApp] : []),
  // Only a true verdict offers the repair. Null means Merge could not be reached, and offering a
  // relink on a connection that may be perfectly healthy sends the customer through the link flow
  // for nothing.
  ...(token.needsRelink === true ? [TicketingConnectionAction.Relink] : []),
  TicketingConnectionAction.Disconnect,
];

/**
 * One row per ticketing connection, with the status wording and the exact set of actions that state
 * admits. Kept away from the components so the state matrix can be read — and tested — in one place.
 *
 * "Superseded" is derived rather than stored: a Merge-managed GitHub connection is superseded
 * exactly when the same company holds a GitHub App connection that is actually active. An armed,
 * suspended, or uninstalled App connection serves no syncs, so the Merge one is still the live one.
 */
export const toTicketingConnectionRows = (
  tokens: CompanyTicketingToken[],
  { gitHubAppAvailable }: { gitHubAppAvailable: boolean },
): TicketingConnectionRow[] => {
  const hasActiveGitHubApp = tokens.some(isActiveGitHubApp);
  const context = { gitHubAppAvailable, hasActiveGitHubApp };

  return tokens
    .filter((token) => token.acquisitionMethod !== TicketingAcquisitionMethod.File)
    .map((token) => ({
      id: token.id,
      provider: TICKETING_PROVIDER_NAMES[token.ticketingProvider],
      method: CONNECTION_METHOD_NAMES[token.acquisitionMethod],
      organization: token.organization ?? null,
      status: resolveStatus(token, context),
      connectedAt: token.createdAt,
      lastSuccessfulSync: token.lastSuccessfulSync,
      actions: resolveActions(token, context),
      token,
    }));
};

/** What the whole list says about one row, beyond the row itself. */
type ConnectionContext = { gitHubAppAvailable: boolean; hasActiveGitHubApp: boolean };

/**
 * "Superseded" is derived rather than stored: a Merge-managed GitHub connection is superseded
 * exactly when the same company holds a GitHub App connection that is actually active. An armed,
 * suspended, or uninstalled App connection serves no syncs, so the Merge one is still the live one.
 */
const isSuperseded = (token: CompanyTicketingToken, { hasActiveGitHubApp }: ConnectionContext): boolean =>
  isMergeGitHub(token) && hasActiveGitHubApp;

const resolveStatus = (token: CompanyTicketingToken, context: ConnectionContext): TicketingConnectionStatus => {
  if (isGitHubApp(token)) {
    return gitHubAppStatus(token.installationStatus ?? null);
  }
  if (isSuperseded(token, context)) {
    return TicketingConnectionStatus.SupersededByGitHubApp;
  }
  return token.needsRelink === true ? TicketingConnectionStatus.NeedsRelink : TicketingConnectionStatus.Connected;
};

const resolveActions = (token: CompanyTicketingToken, context: ConnectionContext): TicketingConnectionAction[] => {
  if (isGitHubApp(token)) {
    return gitHubAppActions(token.installationStatus ?? null, context);
  }

  // A superseded connection no longer serves syncs, so repairing or migrating it is meaningless;
  // removing it is the only thing left to do with it.
  if (isSuperseded(token, context)) {
    return [TicketingConnectionAction.Disconnect];
  }

  if (token.acquisitionMethod === TicketingAcquisitionMethod.Snowflake) {
    return [TicketingConnectionAction.Disconnect];
  }

  return mergeConnectionActions(token, {
    canMigrateToGitHubApp: isMergeGitHub(token) && context.gitHubAppAvailable && !context.hasActiveGitHubApp,
  });
};
